import { createContext, useContext, useRef, useState } from 'react';

// Definisce la "forma" del context: cosa espone agli altri componenti
interface SpinnerContextType {
  showSpinner: () => void;
  hideSpinner: () => void;
  loading: boolean;
}

// Crea il context con valori di default (usati solo se qualcuno
// chiama useSpinner() fuori da un SpinnerProvider)
const SpinnerContext = createContext<SpinnerContextType>({ 
  showSpinner: () => {}, 
  hideSpinner: () => {},
  loading: false
});

export function SpinnerProvider({ children }: { children: React.ReactNode }) {
  
  // loading è l'unico stato "visibile" alla UI:
  // quando cambia, React fa re-render e lo Spinner appare/sparisce
  const [loading, setLoading] = useState(false);

  // activeRequests è un contatore INVISIBILE alla UI (useRef non causa re-render)
  // tiene traccia di quante richieste HTTP sono ancora in corso
  // es: App.tsx fa fetch + RouteManager fa fetch → activeRequests = 2
  const activeRequests = useRef(0);

  // Chiamato all'inizio di ogni fetch (nel try, prima della chiamata API)
  const showSpinner = () => {
    activeRequests.current += 1;  // una richiesta in più è partita
    setLoading(true);              // accende lo spinner
  };

  // Chiamato alla fine di ogni fetch (nel finally, dopo la chiamata API)
  const hideSpinner = () => {
    // decrementa il contatore, Math.max evita che vada sotto 0 per sicurezza
    activeRequests.current = Math.max(0, activeRequests.current - 1);
    
    // spegne lo spinner SOLO se non ci sono altre richieste ancora in corso
    // es: App.tsx finisce ma RouteManager è ancora in attesa → spinner rimane ON
    if (activeRequests.current === 0) {
      setLoading(false);
    }
  };

  return (
    // Rende showSpinner, hideSpinner e loading disponibili
    // a tutti i componenti figli tramite useSpinner()
    <SpinnerContext.Provider value={{ showSpinner, hideSpinner, loading }}>
      {children}
    </SpinnerContext.Provider>
  );
}

// Hook custom: permette ai componenti di accedere al context
// con una sintassi semplice → const { showSpinner } = useSpinner()
export const useSpinner = () => useContext(SpinnerContext);
